import { Observable } from 'rxjs';

import { FilterTypesEnum } from '../enums/filters-types.enum';
import { ChipModel } from '../../Forms/models/chip.model';
import { FilterQuickOptionModel } from './filter-quick-option.model';
import { IncludeExcludeEnum } from '../enums/include-exclude.enum';

export abstract class FilterTypeModel<T = any> {
    abstract key: FilterTypesEnum;
    id: string;
    label: string;
    loading: boolean = false;
    section: string;
    includeExclude: IncludeExcludeEnum = IncludeExcludeEnum.include;
    currentIncludeExclude: IncludeExcludeEnum = IncludeExcludeEnum.include;
    showIncludeExclude: boolean;
    totalResults: number = 0;

    protected abstract value: T;
    protected abstract currentValue: T;

    chips: ChipModel[];
    currentChips: ChipModel[];

    useExactQuickSearchString: boolean = false;

    constructor(id: string, label: string, section?: string, showIncludeExclude?: boolean) {
        this.id = id;
        this.label = label;
        this.section = section;
        this.showIncludeExclude = showIncludeExclude || false;
    }

    /**
     * Sets the active value for a filter
     * @param value
     */
    abstract setValue(value: T): void;
    /**
     * Gets the active value for a filter
     */
    abstract getValue(): T;
    /**
     * Handles deleting a chip for the active value of this filter
     * @param chip A chip from this filter
     */
    abstract removeChip(chip: ChipModel): void;
    /**
     * Resets the filter to have no filters applied
     */
    abstract clear(): void;

    /**
     * Sets the current value for a filter
     * @param value
     */
    abstract setCurrentValue(value: T): void;
    /**
     * Gets the current value for a filter
     */
    abstract getCurrentValue(): T;
    /**
     * Handles deleting a chip for the current value of this filter
     * @param chip A chip from this filter
     */
    abstract removeCurrentChip(chip: ChipModel): void;

    /**
     * Resets the current value to the active value
     */
    abstract cancelChanges(): void;

    /**
     * Gets options available to the quick filter based on the query
     * @param query The value the user typed into the autocomplete box
     * @param translateMethod A method that will tranform a key into the localized string
     * @returns A quick filter option
     */
    quickAutocomplete(query: string, translateMethod: (str: string) => string): FilterQuickOptionModel[] | Observable<FilterQuickOptionModel[]> {
        // default to no results because that is the equivalent of unimplemented
        return [];
    }
    /**
     * Adds the selected option from the quick filter
     * @param option A quick filter option for this filter
     */
    addQuickOption(option: FilterQuickOptionModel): void {
        throw new Error('Not Implemented');
    }

    isInclude(): boolean {
        return this.includeExclude !== IncludeExcludeEnum.exclude;
    }

    setIncludeExclude(includeExclude: IncludeExcludeEnum): void {
        this.includeExclude = includeExclude;
        this.updateChipSuffix();
        this.setCurrentIncludeExclude(includeExclude);
    }

    setCurrentIncludeExclude(includeExclude: IncludeExcludeEnum): void {
        this.currentIncludeExclude = includeExclude;
        this.updateCurrentChipSuffix();
    }

    updateChipSuffix() {
        const suffix = this.includeExclude ===  IncludeExcludeEnum.exclude ? 'Common.forms.chips.exclude.suffix' : '';
        this.chips?.forEach(chip => chip.suffix = suffix);
    }

    updateCurrentChipSuffix() {
        const suffix = this.currentIncludeExclude ===  IncludeExcludeEnum.exclude ? 'Common.forms.chips.exclude.suffix' : '';
        this.currentChips?.forEach(chip => chip.suffix = suffix);
    }
}
